'use client';

import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

export interface HeroWireframeGlobeProps {
  className?: string;
  position?: 'hero' | 'discovery' | 'host' | 'center';
  opacityMultiplier?: number;
  isFixed?: boolean;
  /** Whether the globe should stay visible all the way down to the footer (default: true when fixed) */
  showUntilFooter?: boolean;
  /** Optional scroll position (in px) at which globe starts fading out */
  fadeStartPx?: number;
  /** Optional scroll position (in px) at which globe is completely faded out */
  fadeEndPx?: number;
}

export function HeroWireframeGlobe({
  className = '',
  position = 'hero',
  opacityMultiplier = 1.0,
  isFixed = false,
  showUntilFooter = true,
  fadeStartPx,
  fadeEndPx,
}: HeroWireframeGlobeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Scroll listener for fading out globe near the very bottom/footer if desired
  // Uses requestAnimationFrame and direct style mutation to prevent React unmount/remount churn
  useEffect(() => {
    if (!isFixed) return;

    let ticking = false;

    const updateOpacity = () => {
      if (!containerRef.current) {
        ticking = false;
        return;
      }

      const scrollY = window.scrollY || window.pageYOffset || 0;
      let opacity = 1;

      if (!showUntilFooter && typeof fadeStartPx === 'number' && typeof fadeEndPx === 'number') {
        if (scrollY <= fadeStartPx) {
          opacity = 1;
        } else if (scrollY >= fadeEndPx) {
          opacity = 0;
        } else {
          const factor = 1 - (scrollY - fadeStartPx) / (fadeEndPx - fadeStartPx);
          opacity = Math.max(0, Math.min(1, factor));
        }
      } else {
        // Stay steady and visible all the way down through the entire page.
        // Optional subtle fade right before reaching footer (bottom 300px of the document)
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight > 600 && scrollY > docHeight - 250) {
          const fadeProgress = (scrollY - (docHeight - 250)) / 250;
          opacity = Math.max(0, 1 - fadeProgress);
        } else {
          opacity = 1;
        }
      }

      containerRef.current.style.opacity = String(opacity);
      containerRef.current.style.visibility = opacity <= 0.001 ? 'hidden' : 'visible';
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateOpacity);
        ticking = true;
      }
    };

    updateOpacity();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isFixed, showUntilFooter, fadeStartPx, fadeEndPx]);

  useEffect(() => {
    if (!containerRef.current || !canvasRef.current) return;

    // Check prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const container = containerRef.current;
    const canvas = canvasRef.current;
    let width = container.clientWidth || 600;
    let height = container.clientHeight || 600;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 5.2);

    let renderer: THREE.WebGLRenderer | null = null;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    } catch {
      return;
    }

    // Group for globe rotation
    const globeGroup = new THREE.Group();
    // Tilt globe naturally on its axis
    globeGroup.rotation.x = 0.28;
    globeGroup.rotation.z = -0.15;
    // Scale globe: scaled up ~28% from center (0.72 -> 0.92) to balance hero section presence
    const globeScale = isFixed ? 0.92 : 1.0;
    globeGroup.scale.set(globeScale, globeScale, globeScale);
    scene.add(globeGroup);

    // 1. Sparse, intentional node dots placed at wireframe sphere intersections
    // Extract vertices directly from wireframe sphere geometry (18 width segments x 12 height segments)
    const radius = 2.05;
    const wireframeSegmentsW = 18;
    const wireframeSegmentsH = 12;
    const sphereRadius = radius * 0.995;

    // Sample distinct intersection points: sample every 2nd or 3rd intersection to reduce density by ~65-70%
    const nodeCoords: THREE.Vector3[] = [];
    const nodeSizes: number[] = [];
    const nodeColorsList: THREE.Color[] = [];

    const colorDeepGreen = new THREE.Color('#4D5E41');
    const colorPrimary = new THREE.Color('#5F6F52');
    const colorMoss = new THREE.Color('#445439');
    const colorSage = new THREE.Color('#647858');

    // Generate vertices at latitude / longitude intersections
    for (let latIndex = 1; latIndex < wireframeSegmentsH; latIndex++) {
      const theta = (latIndex * Math.PI) / wireframeSegmentsH; // 0 to PI
      const isMajorLat = latIndex % 2 === 0;

      for (let lonIndex = 0; lonIndex < wireframeSegmentsW; lonIndex++) {
        // Sample every 2nd longitude, or every 3rd on minor latitudes
        const shouldInclude = isMajorLat ? lonIndex % 2 === 0 : lonIndex % 3 === 0;
        if (!shouldInclude) continue;

        const phi = (lonIndex * 2 * Math.PI) / wireframeSegmentsW; // 0 to 2*PI

        const x = sphereRadius * Math.sin(theta) * Math.cos(phi);
        const y = sphereRadius * Math.cos(theta);
        const z = sphereRadius * Math.sin(theta) * Math.sin(phi);

        nodeCoords.push(new THREE.Vector3(x, y, z));

        // Vary dot size: major intersection nodes are slightly larger (emphasized data nodes), others subtle
        const isProminent = (latIndex + lonIndex) % 4 === 0;
        nodeSizes.push(isProminent ? 0.085 : 0.052);

        const chosenColor = isProminent ? colorDeepGreen : (latIndex % 3 === 0 ? colorSage : colorPrimary);
        nodeColorsList.push(chosenColor);
      }
    }

    // Also add poles
    nodeCoords.push(new THREE.Vector3(0, sphereRadius, 0));
    nodeSizes.push(0.088);
    nodeColorsList.push(colorDeepGreen);

    nodeCoords.push(new THREE.Vector3(0, -sphereRadius, 0));
    nodeSizes.push(0.088);
    nodeColorsList.push(colorDeepGreen);

    const particleCount = nodeCoords.length;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = nodeCoords[i].x;
      positions[i * 3 + 1] = nodeCoords[i].y;
      positions[i * 3 + 2] = nodeCoords[i].z;

      colors[i * 3] = nodeColorsList[i].r;
      colors[i * 3 + 1] = nodeColorsList[i].g;
      colors[i * 3 + 2] = nodeColorsList[i].b;

      sizes[i] = nodeSizes[i];
    }

    const pointsGeometry = new THREE.BufferGeometry();
    pointsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    pointsGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Circular soft point texture
    const pointCanvas = document.createElement('canvas');
    pointCanvas.width = 32;
    pointCanvas.height = 32;
    const ctx = pointCanvas.getContext('2d');
    if (ctx) {
      const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
      grad.addColorStop(0, 'rgba(255,255,255,1)');
      grad.addColorStop(0.55, 'rgba(255,255,255,0.85)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(16, 16, 16, 0, Math.PI * 2);
      ctx.fill();
    }
    const pointTexture = new THREE.CanvasTexture(pointCanvas);
    pointTexture.flipY = false;
    pointTexture.premultiplyAlpha = false;

    // Clean node points material: clean and sparse
    const pointsMaterial = new THREE.PointsMaterial({
      size: 0.075,
      vertexColors: true,
      map: pointTexture,
      transparent: true,
      opacity: 0.85 * opacityMultiplier,
      blending: THREE.NormalBlending,
      depthWrite: false,
    });

    const pointsMesh = new THREE.Points(pointsGeometry, pointsMaterial);
    globeGroup.add(pointsMesh);

    // 2. Clearly visible stylized wireframe latitudes & longitudes
    const wireframeGeo = new THREE.SphereGeometry(radius * 0.995, 18, 12);
    const wireframeMat = new THREE.MeshBasicMaterial({
      color: 0x4d5e41,
      wireframe: true,
      transparent: true,
      opacity: 0.22 * opacityMultiplier,
    });
    const wireframeMesh = new THREE.Mesh(wireframeGeo, wireframeMat);
    globeGroup.add(wireframeMesh);

    // 3. Clearly visible orbital ring around equator
    const ringGeo = new THREE.RingGeometry(radius * 1.08, radius * 1.095, 64);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x4d5e41,
      transparent: true,
      opacity: 0.28 * opacityMultiplier,
      side: THREE.DoubleSide,
    });
    const ringMesh = new THREE.Mesh(ringGeo, ringMat);
    ringMesh.rotation.x = Math.PI / 2 + 0.2;
    globeGroup.add(ringMesh);

    // Animation loop: micro-rotation (~80 seconds for a full 360° turn)
    let animationFrameId: number;
    let isDisposed = false;

    const animate = () => {
      if (isDisposed) return;
      if (!prefersReducedMotion) {
        globeGroup.rotation.y += 0.00075;
      }
      if (renderer) {
        renderer.render(scene, camera);
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Resize listener
    const handleResize = () => {
      if (!containerRef.current || !renderer) return;
      width = containerRef.current.clientWidth;
      height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      isDisposed = true;
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      pointsGeometry.dispose();
      pointsMaterial.dispose();
      wireframeGeo.dispose();
      wireframeMat.dispose();
      ringGeo.dispose();
      ringMat.dispose();
      pointTexture.dispose();
      if (renderer) {
        renderer.dispose();
      }
    };
  }, [opacityMultiplier, isFixed]);

  // Layout alignment and soft masks
  let alignClasses = 'flex items-center justify-center';
  let maskStyle: React.CSSProperties = {
    maskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 80%)',
    WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 80%)',
  };

  if (isFixed) {
    // Perfectly centered horizontally and vertically in viewport
    alignClasses = 'flex items-center justify-center';
    maskStyle = {
      // Clean circular radial gradient mask ensuring the full sphere shape is preserved
      // with soft feathering along the outer edges to prevent harsh viewport cutoffs
      maskImage:
        'radial-gradient(circle at 50% 50%, black 58%, rgba(0,0,0,0.85) 78%, transparent 98%)',
      WebkitMaskImage:
        'radial-gradient(circle at 50% 50%, black 58%, rgba(0,0,0,0.85) 78%, transparent 98%)',
    };
  } else if (position === 'discovery') {
    alignClasses = 'flex items-center justify-center lg:justify-end lg:pr-[10vw]';
    maskStyle = {
      maskImage:
        'radial-gradient(circle at 60% 50%, black 35%, rgba(0,0,0,0.75) 65%, transparent 88%)',
      WebkitMaskImage:
        'radial-gradient(circle at 60% 50%, black 35%, rgba(0,0,0,0.75) 65%, transparent 88%)',
    };
  } else if (position === 'host') {
    alignClasses = 'flex items-center justify-center lg:justify-start lg:pl-[8vw]';
    maskStyle = {
      maskImage:
        'radial-gradient(circle at 45% 50%, black 35%, rgba(0,0,0,0.75) 65%, transparent 88%)',
      WebkitMaskImage:
        'radial-gradient(circle at 45% 50%, black 35%, rgba(0,0,0,0.75) 65%, transparent 88%)',
    };
  } else if (position === 'hero') {
    alignClasses = 'flex items-center justify-center lg:justify-start lg:pl-[14vw]';
    maskStyle = {
      maskImage:
        'radial-gradient(ellipse 65% 55% at 58% 44%, black 30%, rgba(0,0,0,0.7) 60%, transparent 85%)',
      WebkitMaskImage:
        'radial-gradient(ellipse 65% 55% at 58% 44%, black 30%, rgba(0,0,0,0.7) 60%, transparent 85%)',
    };
  }

  const containerPositionClass = isFixed
    ? 'fixed inset-0 z-0'
    : 'absolute inset-0 z-0';

  // Sizing: calibrated ~28% larger so the sphere is prominent while staying centered and clear of navbar/ticker
  const canvasSizeClass = isFixed
    ? 'w-[360px] h-[360px] sm:w-[480px] sm:h-[480px] md:w-[600px] md:h-[600px] lg:w-[700px] lg:h-[700px]'
    : 'w-[460px] h-[460px] sm:w-[620px] sm:h-[620px] md:w-[780px] md:h-[780px] lg:w-[920px] lg:h-[920px]';

  return (
    <div
      ref={containerRef}
      className={`${containerPositionClass} pointer-events-none overflow-hidden flex items-center justify-center transition-opacity duration-150 will-change-[opacity] ${className}`}
      style={{ opacity: 1 }}
      aria-hidden="true"
    >
      <div className={`relative w-full h-full ${alignClasses}`} style={maskStyle}>
        <canvas
          ref={canvasRef}
          className={`${canvasSizeClass} opacity-90`}
        />
      </div>
    </div>
  );
}
